/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

/**
 * @typedef {object} SwappableItem
 * @property {number} position
 */

/**
 * Takes an array and two indexes and returns a new array
 * with the contents swapped.
 *
 * @param {SwappableItem[]} items
 * @param {number} replacementIndex
 * @param {number} targetIndex
 * @returns {SwappableItem[]}
 */
export const swapItems = (items, replacementIndex, targetIndex) => {
    let newItems = structuredClone(items);
    
    if (replacementIndex === targetIndex) {
        return newItems;
    }

    const lastIndex = newItems.length - 1;

    if (replacementIndex < 0 || replacementIndex > lastIndex) {
        throw new OutOfBoundsError("replacement", replacementIndex, lastIndex);
    }

    if (targetIndex < 0 || targetIndex > lastIndex) {
        throw new OutOfBoundsError("target", targetIndex, lastIndex);
    }

    // more verbose, but is clearer for the operation
    const replacement = newItems[replacementIndex];
    const target = newItems[targetIndex];

    // perform swap
    newItems[replacementIndex] = target;
    newItems[targetIndex] = replacement;

    // update positions
    newItems = newItems.map((item, idx) => {
        item.position = idx;
        return item;
    });

    return newItems;
};

class OutOfBoundsError extends Error {
    /**
     * @param {'replacement' | 'target'} type
     * @param {number} index
     * @param {number} lastIndex
     */
    constructor (type, index, lastIndex) {
        super(`The ${type} index (${index}) is out of bounds: [0,${lastIndex}]`);
    }
}
